import React, { useEffect, useMemo, useState } from "react"
import moment from "moment-timezone"
import { Alert, Box, Button, CircularProgress, Container, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { Add, ContentCopy, Delete, Save } from "@mui/icons-material"
import { DesktopDatePicker, LocalizationProvider } from "@mui/lab"
import AdapterMoment from "@mui/lab/AdapterMoment"
import { ISTableHeaderCell, ISTableRow, ISTableTitle } from "../../../Utils/ISTable"
import { getToken } from "../../../../utils/fetchApi"

const TIMEZONE = "America/Argentina/Buenos_Aires"

type Goal2WeightTier = {
  id?: number
  minClients: number
  maxClients: number | null
  coverageWeight: number
  participationWeight: number
  from?: string
  to?: string
  status?: string
}

type WeightResponse = {
  targetMonth: string
  copiedFrom?: string
  tiers: Goal2WeightTier[]
}

const Goal2WeightTierAdmin: React.FC = () => {
  const [targetMonth, setTargetMonth] = useState(
    moment().tz(TIMEZONE).startOf("month")
  )
  const [tiers, setTiers] = useState<Goal2WeightTier[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const targetMonthKey = targetMonth.format("YYYY-MM")
  const busy = loading || saving || copying

  const request = async (url: string, options?: RequestInit) => {
    const token = getToken()

    if (!token) {
      throw new Error("No se encontró el token de autenticación.")
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        ...(options?.headers ?? {})
      }
    })

    let data: any = {}

    try {
      data = await response.json()
    } catch {
      data = {}
    }

    if (!response.ok) {
      throw new Error(data?.error ?? "Ocurrió un error procesando la solicitud.")
    }

    return data
  }

  const normalizeTiers = (values: Goal2WeightTier[]): Goal2WeightTier[] =>
    [...values]
      .map(tier => ({
        ...tier,
        minClients: Number(tier.minClients ?? 0),
        maxClients: tier.maxClients === null || tier.maxClients === undefined
          ? null
          : Number(tier.maxClients),
        coverageWeight: Number(tier.coverageWeight ?? 0),
        participationWeight: Number(tier.participationWeight ?? 0)
      }))
      .sort((a, b) => a.minClients - b.minClients)

  const loadWeights = async () => {
    setLoading(true)
    setError(null)
    setMessage(null)

    try {
      const params = new URLSearchParams({ targetMonth: targetMonthKey })
      const data = await request(
        `/goals/constant-average/weights?${params.toString()}`
      ) as WeightResponse

      setTiers(normalizeTiers(data.tiers ?? []))
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error obteniendo las ponderaciones."
      )
      setTiers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadWeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMonthKey])

  const updateTier = (
    index: number,
    field: keyof Goal2WeightTier,
    value: number | null
  ) => {
    setMessage(null)
    setError(null)

    setTiers(previous => {
      const copy = previous.map(tier => ({ ...tier }))
      copy[index] = { ...copy[index], [field]: value }
      return copy
    })
  }

  const addTier = () => {
    setMessage(null)
    setError(null)

    setTiers(previous => {
      const copy = normalizeTiers(previous)

      if (copy.length > 0 && copy[copy.length - 1].maxClients === null) {
        setError("Para agregar otra fila, primero indique el máximo del último rango.")
        return previous
      }

      const previousTier = copy.length > 0 ? copy[copy.length - 1] : null
      const minClients =
        previousTier?.maxClients !== null && previousTier?.maxClients !== undefined
          ? previousTier.maxClients + 1
          : 1

      return [
        ...copy,
        {
          minClients,
          maxClients: null,
          coverageWeight: 50,
          participationWeight: 50
        }
      ]
    })
  }

  const removeTier = (index: number) => {
    setMessage(null)
    setError(null)

    setTiers(previous =>
      previous.filter((_, currentIndex) => currentIndex !== index)
    )
  }

  const validationError = useMemo(() => {
    if (tiers.length === 0) {
      return "Debe indicar al menos una fila."
    }

    const sorted = normalizeTiers(tiers)

    if (sorted[0].minClients !== 1) {
      return "La primera fila debe comenzar en 1 cliente."
    }

    for (let index = 0; index < sorted.length; index++) {
      const tier = sorted[index]

      if (!Number.isInteger(tier.minClients) || tier.minClients < 1) {
        return `El mínimo de la fila ${index + 1} es inválido.`
      }

      if (
        tier.maxClients !== null &&
        (!Number.isInteger(tier.maxClients) || tier.maxClients < tier.minClients)
      ) {
        return `El máximo de la fila ${index + 1} es inválido.`
      }

      if (
        tier.coverageWeight < 0 ||
        tier.coverageWeight > 100 ||
        tier.participationWeight < 0 ||
        tier.participationWeight > 100
      ) {
        return `Los pesos de la fila ${index + 1} deben estar entre 0% y 100%.`
      }

      if (
        Math.abs(
          tier.coverageWeight +
          tier.participationWeight -
          100
        ) > 0.0001
      ) {
        return `Cobertura y participación de la fila ${index + 1} deben sumar 100%.`
      }

      if (index > 0) {
        const previous = sorted[index - 1]

        if (previous.maxClients === null) {
          return "Solamente la última fila puede quedar sin máximo."
        }

        if (tier.minClients !== previous.maxClients + 1) {
          return `Debe existir continuidad entre las filas ${index} y ${index + 1}.`
        }
      }
    }

    if (sorted[sorted.length - 1].maxClients !== null) {
      return "El última fila debe quedar abierta, sin cantidad máxima."
    }

    return null
  }, [tiers])

  const saveWeights = async () => {
    if (validationError) {
      setError(validationError)
      return
    }

    setSaving(true)
    setError(null)
    setMessage(null)

    try {
      const normalized = normalizeTiers(tiers)

      const data = await request("/goals/constant-average/weights", {
        method: "PUT",
        body: JSON.stringify({
          targetMonth: targetMonthKey,
          tiers: normalized.map(tier => ({
            minClients: tier.minClients,
            maxClients: tier.maxClients,
            coverageWeight: tier.coverageWeight,
            participationWeight: tier.participationWeight
          }))
        })
      }) as WeightResponse

      setTiers(normalizeTiers(data.tiers ?? []))
      setMessage(
        `Ponderaciones de ${targetMonth.format("MM/YYYY")} guardadas correctamente.`
      )
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error guardando las ponderaciones."
      )
    } finally {
      setSaving(false)
    }
  }

  const copyPrevious = async () => {
    setCopying(true)
    setError(null)
    setMessage(null)

    try {
      const data = await request(
        "/goals/constant-average/weights/copy-previous",
        {
          method: "POST",
          body: JSON.stringify({
            targetMonth: targetMonthKey
          })
        }
      ) as WeightResponse

      setTiers(normalizeTiers(data.tiers ?? []))

      setMessage(
        data.copiedFrom
          ? `Configuración copiada desde ${moment(
            data.copiedFrom,
            "YYYY-MM"
          ).format("MM/YYYY")}.`
          : "Configuración anterior copiada correctamente."
      )
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error copiando la configuración anterior."
      )
    } finally {
      setCopying(false)
    }
  }

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Paper sx={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title="Ponderaciones de objetivos por promedios" />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ padding: 2 }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              views={["month", "year"]}
              inputFormat="MM/YYYY"
              label="Mes"
              value={targetMonth}
              disabled={busy}
              onChange={date => {
                if (date) {
                  setTargetMonth(
                    moment(date).tz(TIMEZONE).startOf("month")
                  )
                }
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  size="small"
                  sx={{ minWidth: 220 }}
                />
              )}
            />
          </LocalizationProvider>

          <Button
            size="large"
            variant="outlined"
            startIcon={<ContentCopy />}
            disabled={busy}
            onClick={copyPrevious}
            style={{ textTransform: "none" }}
          >
            Copiar última configuración
          </Button>

          <Button
            size="large"
            startIcon={<Save />}
            disabled={busy || !!validationError}
            onClick={saveWeights}
            style={{ textTransform: "none" }}
          >
            Guardar
          </Button>
        </Stack>

        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2">
            Los pesos de cobertura y participación de cada fila deben sumar{" "}
            <strong>100%</strong>. La cantidad de clientes corresponde a la base
            histórica utilizada para calcular los objetivos del período.
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ margin: "10px" }}>
          {error}
        </Alert>
      )}

      {message && (
        <Alert severity="success" sx={{ margin: "10px" }}>
          {message}
        </Alert>
      )}

      {validationError && !error && (
        <Alert severity="warning" sx={{ margin: "10px" }}>
          {validationError}
        </Alert>
      )}

      <Paper
        sx={{
          borderRadius: "10px",
          margin: "10px",
          overflow: "hidden"
        }}
      >
        <ISTableTitle title={`Escalas - ${targetMonth.format("MM/YYYY")}`} />

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow selected>
                    <ISTableHeaderCell align="right">
                      Desde clientes
                    </ISTableHeaderCell>

                    <ISTableHeaderCell align="right">
                      Hasta clientes
                    </ISTableHeaderCell>

                    <ISTableHeaderCell align="right">
                      Peso cobertura
                    </ISTableHeaderCell>

                    <ISTableHeaderCell align="right">
                      Peso participación
                    </ISTableHeaderCell>

                    <ISTableHeaderCell align="center">
                      Total
                    </ISTableHeaderCell>

                    <ISTableHeaderCell align="center">
                      Acciones
                    </ISTableHeaderCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tiers.map((tier, index) => {
                    const totalWeight =
                      Number(tier.coverageWeight ?? 0) +
                      Number(tier.participationWeight ?? 0)

                    const validWeight =
                      Math.abs(totalWeight - 100) < 0.0001

                    const isLast = tier.maxClients === null

                    return (
                      <ISTableRow
                        key={tier.id ?? `tier-${index}`}
                        color="default"
                      >
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={tier.minClients}
                            disabled={busy}
                            inputProps={{
                              min: 1,
                              step: 1
                            }}
                            onChange={event =>
                              updateTier(
                                index,
                                "minClients",
                                Number(event.target.value)
                              )
                            }
                            sx={{ width: 130 }}
                          />
                        </TableCell>

                        <TableCell align="right">

                          <TextField
                            type="number"
                            size="small"
                            value={tier.maxClients ?? ""}
                            disabled={busy}
                            inputProps={{
                              min: tier.minClients,
                              step: 1
                            }}
                            placeholder={isLast ? "Sin límite" : undefined}
                            onChange={event =>
                              updateTier(
                                index,
                                "maxClients",
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value)
                              )
                            }
                            sx={{ width: 130 }}
                          />

                        </TableCell>

                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={tier.coverageWeight}
                            disabled={busy}
                            inputProps={{
                              min: 0,
                              max: 100,
                              step: 1
                            }}
                            onChange={event => {
                              const value = Number(event.target.value)

                              setTiers(previous => {
                                const copy = previous.map(item => ({
                                  ...item
                                }))

                                copy[index].coverageWeight = value
                                copy[index].participationWeight = 100 - value

                                return copy
                              })
                            }}
                            InputProps={{ endAdornment: "%" }}
                            sx={{ width: 150 }}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={tier.participationWeight}
                            disabled={busy}
                            inputProps={{
                              min: 0,
                              max: 100,
                              step: 1
                            }}
                            onChange={event => {
                              const value = Number(event.target.value)

                              setTiers(previous => {
                                const copy = previous.map(item => ({
                                  ...item
                                }))

                                copy[index].participationWeight = value
                                copy[index].coverageWeight = 100 - value

                                return copy
                              })
                            }}
                            InputProps={{ endAdornment: "%" }}
                            sx={{ width: 150 }}
                          />
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: "bold",
                            color: validWeight
                              ? "success.main"
                              : "error.main"
                          }}
                        >
                          {totalWeight.toFixed(2)}%
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Eliminar fila">
                            <span>
                              <IconButton
                                size="small"
                                disabled={busy || tiers.length < 1}
                                onClick={() => removeTier(index)}
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </ISTableRow>
                    )
                  })}

                  {tiers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        No existe una configuración para este mes.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={2} sx={{ padding: 2 }}>
              <Button
                startIcon={<Add />}
                disabled={busy}
                onClick={addTier}
                style={{ textTransform: "none" }}
              >
                Agregar fila
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Container>
  )
}

export default Goal2WeightTierAdmin